import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from './Icons';

interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  accent?: string;
  busy?: boolean;
}

/** Accessible bottom sheet dialog (focus moves in, Escape closes, focus restored). */
export function Sheet({ open, title, onClose, children, footer, accent, busy = false }: SheetProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const background = document.getElementById('root');
    const previousInert = background?.inert ?? false;
    if (background) background.inert = true;
    const previousOverflow = document.body.style.overflow;
    const initial = panel.current?.querySelector<HTMLElement>('[data-autofocus]')
      ?? panel.current?.querySelector<HTMLElement>('input, select, textarea')
      ?? panel.current?.querySelector<HTMLElement>('button');
    initial?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) onClose();
      if (e.key === 'Tab') {
        const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? [])
          .sort((a, b) => a === b ? 0 : a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first) { e.preventDefault(); panel.current?.focus(); }
        else if (e.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (background) background.inert = previousInert;
      prev?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-sm animate-fade-up" onClick={() => !busy && onClose()} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-busy={busy}
        aria-labelledby={titleId}
        className="relative flex max-h-[92dvh] w-full max-w-md flex-col rounded-t-[1.75rem] border border-white/10 bg-ink-900 animate-sheet-up"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 rounded-t-[1.75rem] opacity-40"
          style={{ background: `linear-gradient(180deg, ${accent ?? 'var(--color-sleep)'}33, transparent)` }}
        />
        <div className="relative flex items-center justify-between px-5 pt-3">
          <span className="mx-auto mb-1 h-1 w-10 rounded-full bg-white/15" aria-hidden="true" />
        </div>
        <div className="relative flex items-center justify-between px-5 pb-2">
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <button type="button" onClick={onClose} disabled={busy} className="btn-ghost !min-h-11 !w-11 !rounded-full !px-0" aria-label="Close">
            <IconX size={18} />
          </button>
        </div>
        <div className="relative flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && <div className="relative border-t border-white/5 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
